import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonicalUrl?: string;
  ogImage?: string;
  ogType?: string;
  schemaData?: Record<string, unknown> | Record<string, unknown>[];
  noIndex?: boolean;
}

export default function SEOHead({
  title,
  description,
  keywords,
  canonicalUrl,
  ogImage,
  ogType = 'website',
  schemaData,
  noIndex = false,
}: SEOHeadProps) {
  const location = useLocation();
  const baseUrl = 'https://hanmandq.com';
  const currentUrl = `${baseUrl}${location.pathname}`;

  useEffect(() => {
    const defaultTitle = '韩漫大全_好看的韩国漫画_漫画韩漫网址_免费韩漫在线阅读-韩漫漫画网站';
    const defaultDesc = '韩漫漫画网站通过此漫画网址提供韩漫大全与好看韩国漫画的免费在线阅读，资源稳定更新及时。';
    const defaultKeywords = '韩漫大全,好看韩国漫画,漫画网址,免费在线阅读,韩漫漫画网站';

    document.title = title || defaultTitle;

    const setMeta = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', description || defaultDesc);
    setMeta('keywords', keywords || defaultKeywords);
    setMeta('og:title', title || defaultTitle, true);
    setMeta('og:description', description || defaultDesc, true);
    setMeta('og:url', canonicalUrl || currentUrl, true);
    setMeta('og:type', ogType, true);
    if (ogImage) {
      setMeta('og:image', ogImage, true);
    }
    setMeta('twitter:title', title || defaultTitle);
    setMeta('twitter:description', description || defaultDesc);
    if (ogImage) {
      setMeta('twitter:image', ogImage);
    }

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = canonicalUrl || currentUrl;

    if (noIndex) {
      setMeta('robots', 'noindex, follow');
    } else {
      setMeta('robots', 'index, follow');
    }

    const existingScripts = document.querySelectorAll('script[data-seo-jsonld]');
    existingScripts.forEach((s) => s.remove());

    if (schemaData) {
      const dataArray = Array.isArray(schemaData) ? schemaData : [schemaData];
      dataArray.forEach((data) => {
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.setAttribute('data-seo-jsonld', 'true');
        script.textContent = JSON.stringify(data);
        document.head.appendChild(script);
      });
    }

    return () => {
      const scripts = document.querySelectorAll('script[data-seo-jsonld]');
      scripts.forEach((s) => s.remove());
    };
  }, [title, description, keywords, canonicalUrl, ogImage, ogType, schemaData, noIndex, currentUrl]);

  return null;
}